import extendStatus from 'bdata-game-utils/utils/extendStatus';
import PatchableApiModel from './PatchableApiModel';

const HOME = 'home';
const AWAY = 'away';
const TEAM = 'team';
const SPACER = ' ';
const EMPTY = '';
const HYPHEN = '-';
const TOP = 'top';
const BOTTOM = 'bottom';
const FINAL = 'Final';
const INNING_NOT_PLAYED = 'x';

const boxTeamProperties = ['batters', 'battingOrder', 'bench', 'bullpen', 'pitchers'];
const offense = {
    top: AWAY,
    bottom: HOME,
    middle: HOME,
    end: AWAY
};

const defense = {
    top: HOME,
    bottom: AWAY,
    middle: AWAY,
    end: HOME
};

const decisionsMap = {
    winner: 'winner',
    loser: 'loser',
    save: 'winner'
};

const T = 'T';
const _ = '_';
const R_TIMETOTFS = /[-:Z]|\.000/g;

const TWO_WAY_QUALIFYING_SCORE = 10;

class GumboModel extends PatchableApiModel {
    constructor (data, params) {
        super(data);
        this.params = params;
    }

    parseData (data) {
        const { liveData, gameData } = this;
        const teamMap = this.teamMap = {};
        if (liveData) {
            const { boxscore = {}, plays, linescore, decisions } = liveData;
            const {
                players,
                probablePitchers,
                status,
                game: {
                    type: gameType,
                    tiebreaker,
                    doubleHeader = 'N'
                } = {},
                flags
            } = gameData;

            let paddedLinescore;

            if (boxscore.teams) {
                boxscore.teams = this.parseTeams(boxscore.teams, gameData.teams, players, this);
            }

            if (boxscore.topPerformers) {
                boxscore.topPerformers = this.parseTopPerformers(boxscore.topPerformers);
            }

            if (linescore && boxscore.teams) {
                this.parseLinescore(linescore, teamMap);
                paddedLinescore = liveData.paddedLinescore = this.padLinescore(linescore, status, boxscore.teams, plays);
            }

            this.populateProbables(probablePitchers, teamMap);

            if (paddedLinescore?.winner) {
                this.populateDecisions(decisions, teamMap, paddedLinescore);
            }

            const moldedData = { status, gameType, tiebreaker, doubleHeader, flags };
            status.extendedStatus = extendStatus(moldedData);

            if (plays) {
                this.populateResultGuids(plays);
                this.parsePlays(plays, gameData.teams);
            }
        }
    }

    hasInning (playInning, half) {
        return playInning !== null && playInning[half].length > 0;
    }

    timeToTfs (time) {
        if (!time) {
            return SPACER;
        }

        return time.replace(R_TIMETOTFS, EMPTY).replace(T, _);
    }

    // Loop through all plays and surface the play id (guid) of the last
    // play event for easy plug-and-play with the guid-video map for PlayResults
    populateResultGuids (plays = {}) {
        const {
            allPlays = []
        } = plays;

        allPlays.forEach((play) => {
            const {
                playEvents = []
            } = play;
            if (playEvents.length > 0) {
                const lastPlayEvent = playEvents[playEvents.length - 1];
                const {
                    playId
                } = lastPlayEvent;
                if (playId) {
                    play.resultPlayGuid = playId;
                }
            }
        });
    }

    reducePlayerPositions (accumulator, currentPosition = {}) {
        const { abbreviation } = currentPosition;
        if (accumulator === EMPTY) {
            return abbreviation;
        } else {
            return `${accumulator}${HYPHEN}${abbreviation}`;
        }
    }

    populateOffenseDefense (side, teams) {
        let key;
        const team = side?.team && teams[side.team.id];
        const playerById = team?.playerById;
        let fielder;

        if (playerById && side) {
            for (key in side) {
                if (key !== TEAM) {
                    fielder = side[key];
                    side[key] = playerById[fielder.id] || fielder;
                }
            }
        }
    }

    parseLinescore (linescore = {}, teams) {
        const { offense, defense } = linescore;
        this.populateOffenseDefense(offense, teams);
        this.populateOffenseDefense(defense, teams);
    }

    padLinescore (linescore = {}, status = {}, boxscoreTeams, plays = {}) {
        const { abstractGameState, detailedState, statusCode } = status;
        const { playsByInning, allPlays } = plays;
        const { teams, scheduledInnings } = linescore;
        const isFinal = (abstractGameState === FINAL);
        const innings = linescore.innings.slice(0);
        const currentInningIndex = (~~linescore.currentInning) - 1;
        let inning;
        let playInning;
        let homeStartTime;
        let awayStartTime;
        let len = innings.length;
        let i = 0;
        let paddedLinescore = { ...linescore };

        if (len < scheduledInnings) {
            len = scheduledInnings;
        }

        for (; i < len; i++) {
            inning = innings[i];
            playInning = (playsByInning && playsByInning[i]) || null;

            homeStartTime = this.hasInning(playInning, BOTTOM) ? allPlays[playInning.bottom[0]]?.about?.startTfs : SPACER;
            awayStartTime = this.hasInning(playInning, TOP) ? allPlays[playInning.top[0]]?.about?.startTfs : SPACER;

            if (!inning || !inning.home || !inning.away) {
                innings[i] = inning = this.copy(inning, {
                    num: i + 1,
                    home: {
                        runs: inning?.home?.runs || (!inning?.home?.runs && isFinal
                            ? INNING_NOT_PLAYED
                            : SPACER) || SPACER,
                        startTfs: homeStartTime
                    },
                    away: {
                        runs: inning?.away?.runs || (!inning?.away?.runs && isFinal
                            ? INNING_NOT_PLAYED
                            : SPACER) || SPACER,
                        startTfs: awayStartTime
                    }
                });

                if (i === currentInningIndex) {
                    if (inning.home.runs === SPACER) {
                        innings[i].home_active = true;
                    } else if (inning.away.runs === SPACER) {
                        innings[i].away_active = true;
                    }
                }
            } else {
                inning.home.startTfs = homeStartTime;
                inning.away.startTfs = awayStartTime;

                if ((inning.away?.runs === undefined) && isFinal) {
                    inning.away.runs = INNING_NOT_PLAYED;
                }

                if ((inning.home?.runs === undefined) && isFinal) {
                    inning.home.runs = INNING_NOT_PLAYED;
                }
            }
        }

        paddedLinescore = {
            ...paddedLinescore,
            innings,
            status,
            boxscoreTeams,
            displayDetailedStatus: detailedState,
            displayShortStatus: statusCode
        };

        if (status.abstractGameState === FINAL) {
            const awayWins = teams.away.runs > teams.home.runs;
            paddedLinescore = {
                ...paddedLinescore,
                winner: awayWins ? AWAY : HOME,
                loser: awayWins ? HOME : AWAY
            };
        }
        return paddedLinescore;
    }

    populatePlay (play, teams) {
        if (play) {
            const { about = {}, matchup, pitchIndex: pitches, actionIndex: actions, playEvents: events } = play;
            const half = about.halfInning || TOP;
            about.hasPitches = about.hasActions = false;

            if (pitches && pitches.length) {
                about.hasPitches = true;
                play.pitchIndex = pitches.map((pitch) => {
                    const pitchObj = typeof pitch === 'number' ? events[pitch] : pitch;
                    const pitchCode = pitchObj?.details?.code;
                    if (pitchCode) {
                        const details = pitchObj.details;
                        // Working on getting this support in statsapi.  If not present, construct it.
                        details.isInPlayOuts = details.isInPlayOuts ?? (details.isInPlay && (pitchCode === 'Y' || pitchCode === 'X'));
                    }
                    return pitchObj;
                });
            }
            if (actions && actions.length) {
                about.hasActions = true;
                play.actionIndex = actions.map((action) => {
                    return typeof action === 'number' ? events[action] : action;
                });
            }
            about.startTfs = this.timeToTfs(about.startTime);
            about.endTfs = this.timeToTfs(about.endTime);
        }

        return play;
    }

    populatePlayerFromTeam (playerDesc, team) {
        const playerById = team?.playerById;
        return playerDesc?.id && playerById ? playerById[playerDesc.id] : playerDesc;
    }

    extractInning (inning = {}, allPlays) {
        const { top = [], bottom = [] } = inning;
        if (top.length) {
            inning.top = top.map((play) => {
                return typeof play === 'number' ? allPlays[play] : play;
            });
        }
        if (bottom.length) {
            inning.bottom = bottom.map((play) => {
                return typeof play === 'number' ? allPlays[play] : play;
            });
        }

        return inning;
    }

    parseTeams (teams, gameDataTeams, gameDataPlayers, gumbo) {
        let team;
        const teamMap = gumbo.teamMap;

        const processedTeams = {};
        Object.keys(teams).forEach((side) => {
            team = teams[side];
            team.homeOrAway = side;
            team.team = gameDataTeams[side];
            teamMap[team.team.id] = team;
            teamMap[side] = team;
            processedTeams[side] = this.parseBoxLineup(team, gameDataPlayers);
        });
        return processedTeams;
    }

    parseBoxLineup (team, gameDataPlayers) {
        let i = 0;
        const boxProps = boxTeamProperties;
        const len = boxProps.length;
        const { players } = team;
        let id;
        let cleanId;
        let player;
        let allPositionsExists;

        team.playerById = {};

        for (id in players) {
            player = players[id];
            allPositionsExists = !!(player.allPositions && player.allPositions.length);
            cleanId = (player.person = gameDataPlayers[id] || player.person).id;

            team.playerById[cleanId] = player;
            team.playerById[cleanId].displayPositions = allPositionsExists && player.allPositions.reduce(this.reducePlayerPositions, EMPTY);
            team.playerById[cleanId].startingPosition = allPositionsExists && player.allPositions[0];
        }

        for (; i < len; i++) {
            team[boxProps[i]] = team[boxProps[i]].map((id) => {
                if (typeof id === 'number') {
                    return team.playerById[id] || {};
                } else {
                    return id;
                }
            });
        }

        team.battingOrderAll = team.batters?.filter((batter) => !!batter.battingOrder) ?? [];
        return team;
    }

    parsePlays (plays = {}, teams) {
        const { allPlays = [], scoringPlays = [], playsByInning = [], currentPlay } = plays;

        if (allPlays.length) {
            plays.allPlays = allPlays.map((play) => {
                return this.populatePlay(play, teams);
            });
        }

        if (currentPlay) {
            plays.currentPlay = this.populatePlay(currentPlay, teams);
        }
    }

    populateProbables (probables, teams) {
        if (probables) {
            let key;
            for (key in probables) {
                if (probables.hasOwnProperty(key) && teams.hasOwnProperty(key)) {
                    probables[key] = this.populatePlayerFromTeam(probables[key], teams[key]);
                }
            }
        }
    }

    populateDecisions (decisions, teams, paddedLinescore) {
        if (decisions) {
            let key;
            let team;
            for (key in decisions) {
                team = teams[paddedLinescore[decisionsMap[key]]];

                if (decisions.hasOwnProperty(key) && team) {
                    decisions[key] = this.populatePlayerFromTeam(decisions[key], team);
                }
            }
        }
    }

    parseTopPerformers (topPerformers) {
        const players = topPerformers?.map(topPerformer => {
            const {
                player: {
                    person,
                    stats: {
                        batting,
                        ...stats
                    },
                    ...player
                },
                pitchingGameScore,
                hittingGameScore,
                ...restTopPerformer
            } = topPerformer;
            const isTwoWay = pitchingGameScore > TWO_WAY_QUALIFYING_SCORE && hittingGameScore > TWO_WAY_QUALIFYING_SCORE;
            return {
                ...restTopPerformer,
                ...player,
                ...person,
                isTwoWay,
                gameInfo: {
                    box: {
                        hitting: { ...batting },
                        ...stats
                    }
                }
            };
        });
        return players;
    };

    copy (source, target = {}) {
        Object.assign(target, source);
        return target;
    }
}

export default GumboModel;
